import { Browser } from "@capacitor/browser";
import { useCallback } from "react";
import { useTranslations } from "use-intl";
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "@/lib/brand";

/**
 * Terms and privacy footer for the sign-up screen. Reaching both documents is
 * the drawer's Legal section; this line is about consent at the moment an
 * account is created, which is what 5.1.1(ii) asks for. Sign-in carries no
 * such line — there is no new consent to take from a returning user.
 *
 * Nothing here may sit behind the session wall, so it renders with no data of
 * its own. Buttons rather than anchors: inside the Capacitor webview a
 * `target="_blank"` anchor has no tab to open into, so both go through
 * `Browser.open` (SFSafariViewController on iOS, a new tab on the web).
 * Opening synchronously inside the tap keeps WebKit's popup blocker satisfied
 * on the web build.
 */
export function LegalConsent() {
  const t = useTranslations("auth.legalConsent");
  const openPrivacy = useCallback(() => {
    Browser.open({ url: PRIVACY_POLICY_URL });
  }, []);

  const openTerms = useCallback(() => {
    Browser.open({ url: TERMS_OF_SERVICE_URL });
  }, []);

  return (
    <p
      className="text-center text-xs leading-relaxed text-muted-foreground"
      data-testid="legal-consent"
    >
      {t.rich("agreement", {
        privacy: (chunks) => (
          <button
            className="font-medium text-primary underline-offset-2 hover:underline"
            data-testid="legal-consent-privacy"
            onClick={openPrivacy}
            type="button"
          >
            {chunks}
          </button>
        ),
        terms: (chunks) => (
          <button
            className="font-medium text-primary underline-offset-2 hover:underline"
            data-testid="legal-consent-terms"
            onClick={openTerms}
            type="button"
          >
            {chunks}
          </button>
        ),
      })}
    </p>
  );
}
